import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useLocalSearchParams } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  cancelAnimation,
  Easing,
  FadeIn,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { ADS_ENABLED } from '@/config/ads';
import { useThemePalette } from '@/config/theme';
import {
  useBuyExtraSpin,
  useFantasyPlayerPool,
  useFantasySeasons,
  useGrantAdExtraSpin,
  useMyFantasyTeam,
  useSpinState,
  useTakeSpin,
} from '@/features/fantasy/hooks';
import { useAdRewardState } from '@/features/games/rewards';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { PlayerWithTeam } from '@/lib/data/provider';
import { formatPrice, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

interface Nation {
  id: string;
  name: string;
  logo: string | null;
}

const MAX_PICKS = 2;
const TILE = 132;
const BUY_SPIN_COST = 120;

/** Live H:M:S countdown to a target ISO timestamp. */
function useCountdown(targetIso: string | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return 0;
  return Math.max(0, Math.floor((new Date(targetIso).getTime() - now) / 1000));
}

function formatHms(totalSeconds: number, arabicNumerals: boolean): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => localizeDigits(String(n).padStart(2, '0'), arabicNumerals);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function NationFlag({ logo, size }: { logo: string | null; size: number }) {
  const palette = useThemePalette();
  if (!logo) {
    return (
      <View
        className="items-center justify-center rounded-full bg-surface-2"
        style={{ width: size, height: size }}
      >
        <Ionicons name="flag" size={size * 0.4} color={palette.muted} />
      </View>
    );
  }
  return (
    <View className="items-center justify-center overflow-hidden rounded-full bg-surface-2" style={{ width: size, height: size }}>
      <Image source={{ uri: logo }} style={{ width: size, height: size }} contentFit="contain" cachePolicy="memory-disk" />
    </View>
  );
}

export default function SpinScreen() {
  const palette = useThemePalette();
  const params = useLocalSearchParams<{ seasonId: string }>();
  const seasonId = params.seasonId;
  const { t } = useTranslation();
  const { arabicNumerals, numeralOptions } = useLocale();
  const { userId } = useAuth();
  const insets = useSafeAreaInsets();
  const reduce = useReducedMotion();

  const seasonsQuery = useFantasySeasons();
  const season = (seasonsQuery.data ?? []).find((entry) => entry.id === seasonId);
  const poolQuery = useFantasyPlayerPool(season?.competition_id);
  const teamQuery = useMyFantasyTeam(seasonId);
  const spinQuery = useSpinState(seasonId);
  const takeSpin = useTakeSpin();
  const extraSpin = useGrantAdExtraSpin();
  const buySpin = useBuyExtraSpin();
  const toast = useToast();
  const adCapReached = (useAdRewardState().data?.remaining ?? 1) <= 0;
  // Hoisted above the guards so hook order stays stable; only read while waiting.
  const secondsLeft = useCountdown(spinQuery.data?.next_spin_at);

  // Slot-machine state (only meaningful while a spin is available).
  const [phase, setPhase] = useState<'idle' | 'spinning' | 'choosing'>('idle');
  const [displayNationId, setDisplayNationId] = useState<string | null>(null);
  const [chosenNationId, setChosenNationId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pool = useMemo(() => poolQuery.data ?? [], [poolQuery.data]);
  const playerById = useMemo(() => {
    const map = new Map<string, PlayerWithTeam>();
    for (const player of pool) map.set(player.id, player);
    return map;
  }, [pool]);

  const nations = useMemo<Nation[]>(() => {
    const map = new Map<string, Nation>();
    for (const player of pool) {
      const team = player.team;
      if (team?.id && !map.has(team.id)) {
        map.set(team.id, { id: team.id, name: team.name, logo: team.logo_url ?? null });
      }
    }
    return [...map.values()];
  }, [pool]);
  const nationById = useMemo(() => {
    const map = new Map<string, Nation>();
    for (const nation of nations) map.set(nation.id, nation);
    return map;
  }, [nations]);
  // The drawn nation is FIXED for the current spin window (per user + season +
  // day), so re-rolling - or leaving and coming back - always lands on the same
  // nation. The draw is luck, not best-of-N rolls before confirming. The day
  // bucket is captured once at mount (Date.now stays out of the render path).
  const [spinDay] = useState(() => Math.floor(Date.now() / 86_400_000));
  const windowNation = useMemo<Nation | null>(() => {
    if (nations.length === 0) return null;
    const seed = `${seasonId}:${userId ?? ''}:${spinDay}`;
    let h = 0;
    for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return nations[h % nations.length] ?? null;
  }, [nations, seasonId, userId, spinDay]);

  const pulse = useSharedValue(1);
  useEffect(() => {
    if (phase === 'spinning' && !reduce) {
      pulse.value = withRepeat(withTiming(1.06, { duration: 320, easing: Easing.inOut(Easing.ease) }), -1, true);
    } else {
      cancelAnimation(pulse);
      pulse.value = withTiming(1, { duration: 160 });
    }
  }, [phase, reduce, pulse]);
  const tileStyle = useAnimatedStyle(() => ({ transform: [{ scale: pulse.value }] }));

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const randomNation = (): Nation | null =>
    nations.length > 0 ? nations[Math.floor(Math.random() * nations.length)] : null;

  const runSpin = () => {
    if (nations.length === 0) return;
    setError(null);
    setSelected([]);
    setChosenNationId(null);
    setPhase('spinning');
    const total = 18;
    const cycle = (step: number) => {
      if (step >= total) {
        const chosen = windowNation; // deterministic landing - re-roll gives the same nation
        if (!chosen) {
          setPhase('idle');
          return;
        }
        setDisplayNationId(chosen.id);
        setChosenNationId(chosen.id);
        setPhase('choosing');
        return;
      }
      setDisplayNationId(randomNation()?.id ?? null);
      const delay = 50 + step * (320 / total); // ramp ~50ms → ~370ms
      timeoutRef.current = setTimeout(() => cycle(step + 1), delay);
    };
    cycle(0);
  };

  const toggle = (playerId: string) => {
    setSelected((current) => {
      if (current.includes(playerId)) return current.filter((id) => id !== playerId);
      if (current.length >= MAX_PICKS) return current;
      return [...current, playerId];
    });
  };

  const confirm = () => {
    if (selected.length < 1) return;
    setError(null);
    takeSpin.mutate(
      { fantasySeasonId: seasonId, playerIds: selected },
      {
        onSuccess: () => {
          setPhase('idle');
          setChosenNationId(null);
          setSelected([]);
          // Clear the revealed flag so a later ad-granted extra spin shows the
          // neutral idle tile, not the previous spin's nation.
          setDisplayNationId(null);
        },
        onError: (err) => setError(t(errorKey(err))),
      },
    );
  };

  // --- Guards -------------------------------------------------------------
  if (!userId) {
    return (
      <Screen>
        <BackHeader title={t('spin.title')} />
        <EmptyState
          icon="lock-closed-outline"
          title={t('common.signInRequiredTitle')}
          body={t('common.signInRequiredBody')}
        />
      </Screen>
    );
  }

  if (seasonsQuery.isPending || poolQuery.isPending || spinQuery.isPending || teamQuery.isPending) {
    return (
      <Screen>
        <BackHeader title={t('spin.title')} />
        <View className="gap-4 px-4">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-24 w-full" />
        </View>
      </Screen>
    );
  }

  if (seasonsQuery.isError || !season) {
    return (
      <Screen>
        <BackHeader title={t('spin.title')} />
        <ErrorState error={seasonsQuery.error ?? new Error('SEASON_NOT_FOUND')} />
      </Screen>
    );
  }

  if (!teamQuery.data) {
    return (
      <Screen>
        <BackHeader title={t('spin.title')} />
        <EmptyState icon="trophy-outline" title={t('spin.noTeamTitle')} body={t('spin.noTeamBody')} />
      </Screen>
    );
  }

  const state = spinQuery.data;
  const effectiveSeconds = state?.can_spin ? 0 : secondsLeft;
  const activePicks = state?.picks ?? [];
  const chosenNation = chosenNationId ? nationById.get(chosenNationId) : null;
  const displayNation = displayNationId ? nationById.get(displayNationId) : null;
  const chosenPlayers = chosenNation
    ? pool.filter((player) => player.team?.id === chosenNation.id)
    : [];

  return (
    <Screen>
      <BackHeader title={t('spin.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: insets.bottom + 40 }}>
        {/* How it works */}
        <Card className="mx-4 mb-3 flex-row items-start gap-3 px-4 py-3.5">
          <Ionicons name="flash" size={18} color={palette.voltBright} />
          <AppText variant="micro" className="flex-1 leading-5 text-soft">
            {t('spin.howItWorks')}
          </AppText>
        </Card>

        {/* Active boosts */}
        {activePicks.length > 0 ? (
          <Card className="mx-4 mb-3 gap-2.5 px-4 py-3.5">
            <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-volt-bright">
              {t('spin.activeTitle')}
            </AppText>
            {activePicks.map((pick) => {
              const player = playerById.get(pick.player_id);
              if (!player) return null;
              return (
                <View key={pick.player_id} className="flex-row items-center gap-3">
                  <NationFlag logo={player.team?.logo_url ?? null} size={28} />
                  <View className="flex-1">
                    <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                      {player.known_as ?? player.name}
                    </AppText>
                    <AppText variant="micro" className="text-muted" numberOfLines={1}>
                      {player.team?.name} · {t(`player.positions.${player.position}`)}
                    </AppText>
                  </View>
                  <View className="flex-row items-center gap-1 rounded-full bg-volt-glass px-2 py-1">
                    <Ionicons name="flash" size={11} color={palette.voltBright} />
                    <AppText variant="micro" weight="bold" className="text-volt-bright">
                      {t('spin.boosted')}
                    </AppText>
                  </View>
                </View>
              );
            })}
          </Card>
        ) : null}

        {!state?.can_spin && effectiveSeconds > 0 ? (
          // Cooldown countdown
          <Card className="mx-4 mb-3 items-center gap-2 px-4 py-6">
            <Ionicons name="time-outline" size={26} color={palette.voltBright} />
            <AppText variant="caption" className="text-soft">
              {t('spin.nextSpinIn')}
            </AppText>
            <AppText variant="display" weight="bold" tabular className="text-volt-bright">
              {formatHms(effectiveSeconds, arabicNumerals)}
            </AppText>
            {ADS_ENABLED && !adCapReached ? (
              <Pressable
                accessibilityRole="button"
                disabled={extraSpin.isPending}
                onPress={() => extraSpin.mutate(seasonId, { onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }) })}
                className="mt-1 flex-row items-center justify-center gap-1.5 rounded-card border border-win/30 bg-win/10 px-4 py-2 active:opacity-80"
              >
                <Ionicons name={extraSpin.isPending ? 'hourglass-outline' : 'play-circle'} size={16} color={palette.win} />
                <AppText variant="micro" weight="bold" style={{ color: palette.win }}>
                  {t('spin.extraSpinAd')}
                </AppText>
              </Pressable>
            ) : ADS_ENABLED && adCapReached ? (
              <View className="mt-1 flex-row items-center justify-center gap-1">
                <Ionicons name="time-outline" size={13} color={palette.inactive} />
                <AppText variant="micro" className="text-muted">
                  {t('rewards.adCapReached')}
                </AppText>
              </View>
            ) : null}
            <Pressable
              accessibilityRole="button"
              disabled={buySpin.isPending}
              onPress={() => buySpin.mutate(seasonId, { onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }) })}
              className="mt-1 flex-row items-center justify-center gap-1.5 rounded-card border border-volt-line bg-volt-glass px-4 py-2 active:opacity-80"
            >
              <Ionicons
                name={buySpin.isPending ? 'hourglass-outline' : 'server'}
                size={16}
                color={palette.voltBright}
              />
              <AppText variant="micro" weight="bold" className="text-volt-bright">
                {t('spin.buySpin', { coins: localizeDigits(String(BUY_SPIN_COST), arabicNumerals) })}
              </AppText>
            </Pressable>
          </Card>
        ) : (
          // Ready to spin. The spin runs every 24h even during a live round - a
          // small note explains the boost lands on the NEXT round (scoring only
          // counts a boost spun before a gameweek's deadline).
          <View className="items-center px-4 pt-2">
            {state?.gameweek_live ? (
              <Card className="mb-4 w-full flex-row items-center gap-3 px-4 py-3">
                <Ionicons name="information-circle-outline" size={18} color={palette.voltBright} />
                <AppText variant="micro" className="flex-1 text-soft">
                  {t('spin.liveNote')}
                </AppText>
              </Card>
            ) : null}
            <Animated.View style={tileStyle}>
              <View
                className="items-center justify-center rounded-full border-2 border-volt-line bg-volt-glass"
                style={{ width: TILE, height: TILE }}
              >
                {phase === 'idle' && !displayNation ? (
                  <Ionicons name="help" size={44} color={palette.voltBright} />
                ) : (
                  <NationFlag logo={displayNation?.logo ?? null} size={TILE - 28} />
                )}
              </View>
            </Animated.View>
            <View className="h-8 justify-center">
              {phase === 'choosing' && chosenNation ? (
                <Animated.View entering={reduce ? undefined : FadeIn}>
                  <AppText variant="heading" weight="bold" className="text-ink">
                    {chosenNation.name}
                  </AppText>
                </Animated.View>
              ) : phase === 'spinning' ? (
                <AppText variant="caption" className="text-muted">
                  {t('spin.spinning')}
                </AppText>
              ) : null}
            </View>

            {phase !== 'choosing' ? (
              <Button
                label={t('spin.spinCta')}
                loading={phase === 'spinning'}
                disabled={phase === 'spinning' || nations.length === 0}
                onPress={runSpin}
                className="mt-1 w-full"
              />
            ) : null}
          </View>
        )}

        {/* Player chooser after the wheel lands */}
        {phase === 'choosing' && chosenNation ? (
          <View className="mt-4 px-4">
            <View className="mb-2 flex-row items-center justify-between">
              <AppText variant="caption" weight="bold" className="text-soft">
                {t('spin.choosePrompt', { max: localizeDigits(String(MAX_PICKS), arabicNumerals) })}
              </AppText>
              <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
                {localizeDigits(String(selected.length), arabicNumerals)}/{localizeDigits(String(MAX_PICKS), arabicNumerals)}
              </AppText>
            </View>
            <Card className="overflow-hidden p-0">
              {chosenPlayers.map((player, index) => {
                const on = selected.includes(player.id);
                return (
                  <Pressable
                    key={player.id}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    onPress={() => toggle(player.id)}
                    className={`flex-row items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-line' : ''} ${
                      on ? 'bg-volt/10' : ''
                    }`}
                  >
                    <NationFlag logo={player.photo_url ? player.photo_url : (player.team?.logo_url ?? null)} size={32} />
                    <View className="flex-1">
                      <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                        {player.known_as ?? player.name}
                      </AppText>
                      <AppText variant="micro" className="text-muted">
                        {t(`player.positions.${player.position}`)} · {formatPrice(player.fantasy_price, numeralOptions)}
                      </AppText>
                    </View>
                    <Ionicons
                      name={on ? 'checkmark-circle' : 'ellipse-outline'}
                      size={22}
                      color={on ? palette.voltBright : palette.muted}
                    />
                  </Pressable>
                );
              })}
            </Card>

            {error ? (
              <AppText variant="micro" className="mt-3 text-loss">
                {error}
              </AppText>
            ) : null}

            <View className="mt-3 flex-row gap-3">
              <Button
                label={t('spin.confirm')}
                className="flex-1"
                loading={takeSpin.isPending}
                disabled={selected.length < 1 || takeSpin.isPending}
                onPress={confirm}
              />
            </View>
          </View>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
